//@flow
import {
    bulkOrderMasterFetch as bulkOrderMasterFetchApi,
    omsBatch,
    updateOrderStatus as updateOrderStatusApi,
    validateOrderIds as validateOrderIdsApi,
    validateEntityIds as validateEntityIdsApi
} from 'common/middleware/api';
import PaginatedEntityActions from 'common/redux/actions/PaginatedEntityActions';
import type { MasterOrder } from 'common/models/internal/MasterOrder';
import { ToastTypes } from 'common/components/core/toast/Toast';
import { showToast } from 'common/redux/actions/toast';
import I18nHelper from 'common/helpers/I18nHelper';
import { injectError } from 'common/redux/actions/error';
import { ApiError } from 'common/modules/api';
import { ENTITY_NAME_ID } from 'common/constants/entity/orderMaster';
import type { Dispatch } from 'redux';
import type { OrdersDataType } from 'common/redux/selectors/orderMaster';
import type { FilterModelType } from 'common/models/flow/Filter';
import type LocusApiModel from 'common/modules/api';

const translationsNamespace = ['entityManagement', 'orchestrator'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

type Params = {
    clientId: string
};

type Body = {
    filters: Array<FilterModelType>
};

type OrderBody = {
    orderStatus: string,
    orderSelectRequest: {
        selectedIds?: Array<string>,
        filters?: Array<FilterModelType>
    }
};

type OrderIdBody = {
    entityIds: Array<string>
};

class OrderActions extends PaginatedEntityActions<MasterOrder, Params> {
    entityNameId = ENTITY_NAME_ID;
    filterTypes = {
        FETCH_ENTITIES_FILTER_SUCCESS: 'FETCH_ENTITIES_FILTER_SUCCESS'
    };

    constructor(TAG: string, api: LocusApiModel, bulkFetchApi: LocusApiModel) {
        super(TAG, api, bulkFetchApi);
        this.fetchOrders = this.fetchOrders.bind(this);
        this.injectOrders = this.injectOrders.bind(this);
    }

    createBatch = ({
        params,
        body
    }: {
        params: Params,
        body: Object
    }): Function => {
        return (dispatch: Dispatch<*>): Promise<any> => {
            return this.api
                .post(params, body)
                .then((response: any) => {
                    const toast = {
                        message: nsTranslate('createBatchSuccess', {
                            batchId: response.batchId.batchId
                        }),
                        type: ToastTypes.Success
                    };
                    dispatch(showToast(toast, params));
                    return response;
                })
                .catch((error: Error) => {
                    if (error instanceof ApiError) {
                        dispatch(injectError(error));
                        return;
                    }
                    throw error;
                });
        };
    };

    fetchOrders(params: Params, body: Body) {
        return (dispatch: Dispatch<*>) => {
            return this.bulkFetchApi
                .post(params, body)
                .then((response: OrdersDataType) => {
                    dispatch(this.injectOrders(params, response, body));
                    return response;
                });
        };
    }

    updateOrder = ({
        params,
        body,
        totalCount
    }: {
        params: Params,
        body: OrderBody,
        totalCount: number
    }): Function => {
        return (dispatch: Dispatch<*>): Promise<any> => {
            return updateOrderStatusApi
                .post(params, body)
                .then((response: any) => {
                    const toast = {
                        message: nsTranslate(
                            'quotations.orderQuoteRequestSuccessMessage',
                            { count: totalCount }
                        ),
                        type: ToastTypes.Success
                    };
                    dispatch(showToast(toast, params));
                    return response;
                })
                .catch((error: Error) => {
                    if (error instanceof ApiError) {
                        dispatch(injectError(error));
                    }
                    throw error;
                });
        };
    };

    validateOrderIds = ({
        params,
        body
    }: {
        params: Params,
        body: OrderIdBody
    }): Function => {
        return (dispatch: Dispatch<*>): Promise<any> => {
            return validateOrderIdsApi
                .post(params, body)
                .then((response: any) => {
                    return response;
                })
                .catch((error: Error) => {
                    if (error instanceof ApiError) {
                        dispatch(injectError(error));
                        return;
                    }
                    throw error;
                });
        };
    };

    injectOrders(params: Params, entities: OrdersDataType, body: Body) {
        return {
            type: this.filterTypes.FETCH_ENTITIES_FILTER_SUCCESS,
            params,
            entities,
            body
        };
    }
}

export default new OrderActions(
    'Order',
    omsBatch,
    bulkOrderMasterFetchApi,
    null,
    null,
    validateEntityIdsApi
);
